import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Zap,
  TrendingUp,
  Gift,
  CalendarClock,
  Ban,
  Bell,
  Shield,
  ShieldAlert,
  TerminalSquare,
  AlertTriangle,
  Trophy,
  Users,
  ScrollText,
  MessageSquare,
  DoorOpen,
  Crown,
  Server,
  Receipt,
  LifeBuoy,
  LogOut,
  Settings,
  ChevronDown,
  Menu,
  X,
  User,
  KeyRound,
  Headphones,
  SlidersHorizontal,
  Lock,
} from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { Button } from './ui/button';
import { DashboardBrand } from './DashboardBrand';
import { toast } from './ui/sonner';
import { useI18n } from '../contexts/I18nContext';
import { getCommandsSettings, getSystemStatus } from '../lib/api';
import { useAccessibleGuilds } from '../contexts/AccessibleGuildsContext';
import { SYSTEM_SIDEBAR_ITEMS } from '../lib/systemSubpages';
import { buildDashboardAccessContext, canAccessRoute } from '../lib/dashboardAccess';
import { dispatchGuildSelection } from '../lib/guildSelection';
import {
  buildDashboardPath,
  buildLocaleRootPath,
  buildServerPath,
  mapLegacyPathToCanonical,
  normalizeLang,
  parseAppPath,
} from '../lib/routes';

const SIDEBAR_WIDTH_EXPANDED = 280;
const TEMP_VOICE_SECTIONS = ['overview', 'generators', 'controls'];
const NavHighlight = ({ active, group = 'main' }) => active ? (
  <motion.span aria-hidden="true" className="tns-nav-highlight" layoutId={`nav-${group}`}
    transition={{ type: 'spring', stiffness: 380, damping: 34 }} />
) : null;
const sameStringArray = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (String(a[i] || '') !== String(b[i] || '')) return false;
  }
  return true;
};

export const Sidebar = ({ collapsed, setCollapsed, locked = false }) => {
  const animationGroup = useId();
  const { user, logout } = useAuth();
  const { guilds: accessibleGuilds } = useAccessibleGuilds();
  const { t, tr, language } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isRtl = language === 'ar';
  const pathContext = useMemo(() => parseAppPath(location.pathname), [location.pathname]);
  const routeLang = normalizeLang(pathContext.lang || language || 'ar');
  const requestedVoiceSection = new URLSearchParams(location.search).get('section');
  const voiceSection = TEMP_VOICE_SECTIONS.includes(requestedVoiceSection) ? requestedVoiceSection : 'overview';
  const childIsActive = (child, routeActive) => routeActive && (!child.section || child.section === voiceSection);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const [systemStatus, setSystemStatus] = useState({
    levelEnabled: null,
    welcomeEnabled: null,
    automodEnabled: null,
    logsEnabled: null,
    commandsEnabled: null,
    autoRolesEnabled: null,
    tempVoiceEnabled: null,
  });
  const [sectionOpen, setSectionOpen] = useState({
    Server: true,
    Systems: true,
    Settings: true,
    Account: true,
  });
  const [systemOpen, setSystemOpen] = useState({ level: false, logs: false, welcome: false, automod: false, temp_voice: false });
  useEffect(() => {
    if (pathContext.scope !== 'server') return;
    const activeSystem = pathContext.subPath === 'systems/temp-voice' ? 'temp_voice' : Object.entries(SYSTEM_SIDEBAR_ITEMS).find(([, pages]) =>
      pages.some((page) => page.to === `/dashboard/${pathContext.subPath}`),
    )?.[0];
    if (!activeSystem) return;
    setSystemOpen((previous) => previous[activeSystem] ? previous : { ...previous, [activeSystem]: true });
    setSectionOpen((previous) => previous.Systems ? previous : { ...previous, Systems: true });
  }, [pathContext.scope, pathContext.subPath, location.search]);
  const [selectedGuildId, setSelectedGuildId] = useState(null);
  const [selectedGuildAccess, setSelectedGuildAccess] = useState([]);
  const [selectedGuildScopes, setSelectedGuildScopes] = useState([]);
  const effectiveGuildId = useMemo(() => {
    const selected = String(selectedGuildId || '').trim();
    if (selected) return selected;
    if (pathContext.scope === 'server') return String(pathContext.guildId || '').trim();
    return '';
  }, [pathContext.guildId, pathContext.scope, selectedGuildId]);

  useEffect(() => {
    if (pathContext.scope !== 'server' || !pathContext.guildId) return;
    const gid = String(pathContext.guildId).trim();
    if (!gid) return;
    setSelectedGuildId((prev) => (String(prev || '') === gid ? prev : gid));
    localStorage.setItem('tns:selected_guild_id', gid);
  }, [pathContext.guildId, pathContext.scope]);

  useEffect(() => {
    if (!locked) return;
    setCollapsed(true);
  }, [locked, setCollapsed]);

  useEffect(() => {
    if (!isDesktop) setCollapsed(true);
  }, [location.pathname, location.search, isDesktop, setCollapsed]);

  // Listen for guild selection
  useEffect(() => {
    const handleGuildSelected = (event) => {
      const guildId = event.detail?.guild?.id;
      const access = event.detail?.guild?.access_type || [];
      const scopes = event.detail?.guild?.dashboard_scopes || [];
      setSelectedGuildId(guildId);
      const nextAccess = Array.isArray(access) ? access : [];
      const nextScopes = Array.isArray(scopes) ? scopes : [];
      setSelectedGuildAccess((prev) => (sameStringArray(prev, nextAccess) ? prev : nextAccess));
      setSelectedGuildScopes((prev) => (sameStringArray(prev, nextScopes) ? prev : nextScopes));
    };

    // Load from localStorage on mount
    const savedGuildId = localStorage.getItem('tns:selected_guild_id');
    if (savedGuildId) {
      setSelectedGuildId(savedGuildId);
    }
    const savedAccess = localStorage.getItem('tns:selected_guild_access');
    if (savedAccess) {
      try {
        const parsed = JSON.parse(savedAccess);
        const next = Array.isArray(parsed) ? parsed : [];
        setSelectedGuildAccess((prev) => (sameStringArray(prev, next) ? prev : next));
      } catch (e) {
        setSelectedGuildAccess((prev) => (prev.length ? [] : prev));
      }
    }
    const savedScopes = localStorage.getItem('tns:selected_guild_scopes');
    if (savedScopes) {
      try {
        const parsed = JSON.parse(savedScopes);
        const next = Array.isArray(parsed) ? parsed : [];
        setSelectedGuildScopes((prev) => (sameStringArray(prev, next) ? prev : next));
      } catch (e) {
        setSelectedGuildScopes((prev) => (prev.length ? [] : prev));
      }
    }

    window.addEventListener('tns:guild-selected', handleGuildSelected);
    return () => window.removeEventListener('tns:guild-selected', handleGuildSelected);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const neutral = {
      levelEnabled: null,
      welcomeEnabled: null,
      automodEnabled: null,
      logsEnabled: null,
      commandsEnabled: null,
      autoRolesEnabled: null,
      tempVoiceEnabled: null,
    };

    const loadSystemStatus = async () => {
      const gid = String(selectedGuildId || '').trim();
      if (!gid) {
        if (!cancelled) setSystemStatus(neutral);
        return;
      }
      try {
        const [payload, commandPayload] = await Promise.all([
          getSystemStatus(),
          getCommandsSettings().catch(() => ({})),
        ]);
        if (cancelled) return;
        setSystemStatus({
          tempVoiceEnabled: typeof payload?.temp_voice_enabled === 'boolean' ? payload.temp_voice_enabled : null,
          levelEnabled:
            typeof payload?.level_enabled === 'boolean' ? payload.level_enabled : null,
          welcomeEnabled:
            typeof payload?.welcome_enabled === 'boolean' ? payload.welcome_enabled : null,
          automodEnabled:
            typeof payload?.automod_enabled === 'boolean' ? payload.automod_enabled : null,
          logsEnabled:
            typeof payload?.logs_enabled === 'boolean' ? payload.logs_enabled : null,
          commandsEnabled:
            typeof commandPayload?.general_page_enabled === 'boolean' ||
            typeof commandPayload?.moderation_page_enabled === 'boolean'
              ? Boolean(commandPayload?.general_page_enabled ?? true) ||
                Boolean(commandPayload?.moderation_page_enabled ?? true)
              : null,
          autoRolesEnabled:
            typeof commandPayload?.auto_roles?.enabled === 'boolean'
              ? Boolean(commandPayload.auto_roles.enabled)
              : null,
        });
      } catch {
        if (!cancelled) setSystemStatus(neutral);
      }
    };

    const handleRefresh = () => {
      loadSystemStatus();
    };

    loadSystemStatus();
    window.addEventListener('tns:guild-selected', handleRefresh);
    window.addEventListener('tns:settings-updated', handleRefresh);
    window.addEventListener('tns:welcome-settings-updated', handleRefresh);
    window.addEventListener('tns:automod-settings-updated', handleRefresh);
    window.addEventListener('tns:temp-voice-settings-updated', handleRefresh);

    return () => {
      cancelled = true;
      window.removeEventListener('tns:guild-selected', handleRefresh);
      window.removeEventListener('tns:settings-updated', handleRefresh);
      window.removeEventListener('tns:welcome-settings-updated', handleRefresh);
      window.removeEventListener('tns:automod-settings-updated', handleRefresh);
      window.removeEventListener('tns:temp-voice-settings-updated', handleRefresh);
    };
  }, [selectedGuildId]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const onChange = (event) => setIsDesktop(Boolean(event?.matches));
    setIsDesktop(media.matches);
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  useEffect(() => {
    if (!selectedGuildId) return;
    const match = (Array.isArray(accessibleGuilds) ? accessibleGuilds : []).find(
      (guild) => String(guild?.id || '') === String(selectedGuildId),
    );
    const access = Array.isArray(match?.access_type) ? match.access_type : [];
    const scopes = Array.isArray(match?.dashboard_scopes) ? match.dashboard_scopes : [];
    setSelectedGuildAccess((prev) => (sameStringArray(prev, access) ? prev : access));
    setSelectedGuildScopes((prev) => (sameStringArray(prev, scopes) ? prev : scopes));
    localStorage.setItem('tns:selected_guild_access', JSON.stringify(access));
    localStorage.setItem('tns:selected_guild_scopes', JSON.stringify(scopes));
  }, [accessibleGuilds, selectedGuildId]);

  const levelSystemEnabled = systemStatus.levelEnabled;
  const logsSystemEnabled = systemStatus.logsEnabled;
  const xpPagesEnabled = typeof systemStatus.levelEnabled === 'boolean' ? systemStatus.levelEnabled : true;

  const sectionTitle = useMemo(() => {
    const sectionLabels = {
      Server: 'الخادم',
      Systems: 'الأنظمة',
      Settings: 'الإعدادات',
      Account: 'الحساب',
    };
    return (title) => t(sectionLabels[title] || title, title);
  }, [t]);

  const itemLabel = useMemo(() => {
    const labels = {
      'Server Overview': 'نظرة عامة على الخادم',
      Overview: 'نظرة عامة',
      'Room Creators': 'قنوات إنشاء الغرف',
      'Control Panel': 'لوحة التحكم',
      'XP Overview': 'نظرة عامة على XP',
      Leaderboard: 'المتصدرون',
      'XP Settings': 'إعدادات XP',
      'Level System': 'نظام المستويات',
      'Auto Moderation System': 'نظام الرقابة التلقائية',
      'Level Customization': 'تخصيص المستويات',
      Seasons: 'المواسم',
      'Rank • Podium': 'الرانك • البوديوم',
      'XP Bonus': 'بونص XP',
      'XP Exclusion Control': 'الأستثناءات',
      Exclusions: 'الاستثناءات',
      Notifications: 'الإشعارات',
      'User Management': 'إدارة المستخدمين',
      'Logs System': 'نظام السجلات',
      'Welcome System': 'نظام الترحيب',
      'Welcome Message': 'رسالة الترحيب',
      'Leave Message': 'رسالة الخروج',
      'Commands System': 'نظام الأوامر',
      'General Commands': 'الأوامر العامة',
      'Moderation Commands': 'أوامر الإشراف',
      'Auto Roles': 'الرولات التلقائية',
      Messages: 'الرسائل',
      'Embed Messages': 'رسائل الإيمبد',
      'Server Settings': 'إعدادات الخادم',
      'Admin Roles': 'صلاحيات المشرفين',
      'User Profile': 'الملف الشخصي',
      Profile: 'الملف الشخصي',
      Subscription: 'الاشتراك',
      'Activity Log': 'سجل النشاط',
      'My Servers': 'خوادمي',
      Billing: 'الفواتير',
      'Support & FAQ': 'الدعم والأسئلة الشائعة',
    };
    return (label) => {
      if (label === 'Moderation Systems') return t('أنظمة الرقابة', 'Moderation Systems');
      if (label === 'Auto Moderation') return t('الإشراف التلقائي', 'Auto Moderation');
      if (label === 'Violation Points System') return t('نظام نقاط المخالفات', 'Violation Points System');
      if (label === 'Welcome System') return tr('sidebar.systems.welcome', 'Welcome System');
      if (label === 'Temp Voice') return t('الغرف الصوتية المؤقتة', 'Temp Voice');
      if (label === 'Welcome Message') return tr('sidebar.systems.welcomeMessage', 'Welcome Message');
      if (label === 'Leave Message') return tr('sidebar.systems.leaveMessage', 'Leave Message');
      if (label === 'Commands System') return t('نظام الأوامر', 'Commands System');
      if (label === 'General Commands') return t('الأوامر العامة', 'General Commands');
      if (label === 'Moderation Commands') return t('أوامر الإشراف', 'Moderation Commands');
      if (label === 'Auto Roles') return t('الرولات التلقائية', 'Auto Roles');
      return t(labels[label] || label, label);
    };
  }, [t, tr]);

  const accessContext = useMemo(
    () =>
      buildDashboardAccessContext({
        access_type: selectedGuildAccess,
        dashboard_scopes: selectedGuildScopes,
      }),
    [selectedGuildAccess, selectedGuildScopes],
  );

  const showLockedToast = useMemo(
    () => () => {
      toast.error(t('لا تملك صلاحية الوصول لهذه الصفحة.', 'You do not have permission to access this page.'));
    },
    [t],
  );

  const wrapperTransition = useMemo(() => ({ type: 'spring', stiffness: 320, damping: 34, mass: 0.85 }), []);
  const labelTransition = useMemo(() => ({ duration: 0.2 }), []);
  const expandCollapseMotion = useMemo(
    () => (collapsedNow) => ({ opacity: collapsedNow ? 0 : 1 }),
    []
  );
  const renderNavItems = (items) => {
    if (!items) return null;
    return items.map((item) => {
      const disabled = Boolean(item.disabled);
      const target = String(item.to || '').trim();
      const lockedLink = Boolean(item.locked);
      const lockedReason = item.lockReason || '';
      if (disabled) {
        return (
          <div
            key={target}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground/50 bg-secondary/10 cursor-not-allowed transition-colors duration-200 ease-tns"
            data-testid={`sidebar-disabled-${item.label}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-body text-sm whitespace-nowrap">{itemLabel(item.label)}</span>
          </div>
        );
      }

      return (
        <NavLink
          key={target}
          to={target}
          onClick={(event) => {
            if (!lockedLink) return;
            event.preventDefault();
            event.stopPropagation();
            showLockedToast();
          }}
          aria-disabled={lockedLink}
          end={Boolean(item.end || target.endsWith('/server-overview'))}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ease-tns focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
              lockedLink
                ? 'text-muted-foreground/60 bg-secondary/15 opacity-70 cursor-not-allowed'
                : isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
            }`
          }
          title={lockedLink ? lockedReason : undefined}
        >
          {({ isActive }) => <>
            <NavHighlight active={isActive && !lockedLink} />
            <item.icon className="w-5 h-5" />
            <span className="font-body text-sm whitespace-nowrap flex-1">{itemLabel(item.label)}</span>
            {lockedLink ? <Lock className="w-3.5 h-3.5 shrink-0 text-muted-foreground/80" /> : null}
          </>}
        </NavLink>
      );
    });
  };

  const navSections = useMemo(() => {
    const deniedReason = t('لا تملك صلاحية الوصول لهذه الصفحة.', 'You do not have permission to access this page.');
    const withLockedState = (item, rule) => {
      const locked = !canAccessRoute(accessContext, rule);
      return {
        ...item,
        locked,
        lockReason: locked ? deniedReason : '',
      };
    };

    // If no guild selected, show user profile navigation without a category header
    if (pathContext.scope === 'dashboard' || !effectiveGuildId) {
      return [
        {
          id: 'profile-root',
          title: '',
          items: [
            { to: buildDashboardPath(routeLang, 'profile'), icon: User, label: 'Profile', end: true },
            { to: buildDashboardPath(routeLang, 'subscription'), icon: Crown, label: 'Subscription' },
            { to: buildDashboardPath(routeLang, 'servers'), icon: Server, label: 'My Servers' },
            { to: buildDashboardPath(routeLang, 'billing'), icon: Receipt, label: 'Billing' },
            { to: buildDashboardPath(routeLang, 'support'), icon: LifeBuoy, label: 'Support & FAQ' },
          ],
        },
      ];
    }

    // Full navigation when guild is selected (WITHOUT Account section)
    const systems = [
      {
        id: 'temp_voice',
        to: buildServerPath(routeLang, effectiveGuildId, 'systems/temp-voice'),
        icon: Headphones,
        label: 'Temp Voice',
        enabled: systemStatus.tempVoiceEnabled,
        requiredScope: 'temp_voice_system',
        items: [
          { section: 'overview', label: 'Overview', icon: LayoutDashboard },
          { section: 'generators', label: 'Room Creators', icon: DoorOpen },
          { section: 'controls', label: 'Control Panel', icon: SlidersHorizontal },
        ].map((item) => ({
          ...item,
          to: `${buildServerPath(routeLang, effectiveGuildId, 'systems/temp-voice')}?section=${item.section}`,
        })),
      },
      {
        id: 'welcome',
        to: buildServerPath(routeLang, effectiveGuildId, 'systems/welcome'),
        icon: DoorOpen,
        label: 'Welcome System',
        enabled: systemStatus.welcomeEnabled,
        requiredScope: 'welcome_system',
        items: SYSTEM_SIDEBAR_ITEMS.welcome.map((item) => ({
          ...item,
          to: mapLegacyPathToCanonical(item.to, routeLang, effectiveGuildId),
        })),
      },
      {
        id: 'level',
        to: buildServerPath(routeLang, effectiveGuildId, 'systems/level'),
        icon: TrendingUp,
        label: 'Level System',
        enabled: levelSystemEnabled,
        requiredScope: 'level_system',
        items: SYSTEM_SIDEBAR_ITEMS.level.map((item) => ({
          ...item,
          to: mapLegacyPathToCanonical(item.to, routeLang, effectiveGuildId),
        })),
      },
      {
        id: 'automod',
        to: buildServerPath(routeLang, effectiveGuildId, 'systems/automod'),
        icon: Shield,
        label: 'Moderation Systems',
        enabled: systemStatus.automodEnabled,
        requiredScope: 'automod_system',
        items: SYSTEM_SIDEBAR_ITEMS.automod.map((item) => ({
          ...item,
          to: mapLegacyPathToCanonical(item.to, routeLang, effectiveGuildId),
        })),
      },
      {
        id: 'logs',
        to: buildServerPath(routeLang, effectiveGuildId, 'systems/logs'),
        icon: ScrollText,
        label: 'Logs System',
        enabled: logsSystemEnabled,
        requiredScope: 'logs_system',
        items: SYSTEM_SIDEBAR_ITEMS.logs.map((item) => ({
          ...item,
          to: mapLegacyPathToCanonical(item.to, routeLang, effectiveGuildId),
        })),
      },
      {
        id: 'commands',
        to: buildServerPath(routeLang, effectiveGuildId, 'systems/commands/general'),
        icon: TerminalSquare,
        label: 'Commands System',
        enabled: systemStatus.commandsEnabled,
        requiredScope: 'commands_system',
        items: SYSTEM_SIDEBAR_ITEMS.commands.map((item) => ({
          ...item,
          to: mapLegacyPathToCanonical(item.to, routeLang, effectiveGuildId),
        })),
      },
      {
        id: 'auto_roles',
        to: buildServerPath(routeLang, effectiveGuildId, 'systems/auto-roles'),
        icon: Users,
        label: 'Auto Roles',
        enabled: systemStatus.autoRolesEnabled,
        requiredScope: 'commands_system',
        items: SYSTEM_SIDEBAR_ITEMS.auto_roles.map((item) => ({
          ...item,
          to: mapLegacyPathToCanonical(item.to, routeLang, effectiveGuildId),
        })),
      },
    ].map((system) => {
      const systemLocked = !canAccessRoute(accessContext, { requiredScope: system.requiredScope });
      return {
        ...system,
        locked: systemLocked,
        lockReason: systemLocked ? deniedReason : '',
        items: Array.isArray(system.items)
          ? system.items.map((item) => ({
              ...item,
              locked: systemLocked,
              lockReason: systemLocked ? deniedReason : '',
            }))
          : [],
      };
    });

    const serverItems = [
      { to: buildServerPath(routeLang, effectiveGuildId, 'server-overview'), icon: LayoutDashboard, label: 'Server Overview' },
      { to: buildServerPath(routeLang, effectiveGuildId, 'xp-overview'), icon: Zap, label: 'XP Overview', disabled: !xpPagesEnabled },
      { to: buildServerPath(routeLang, effectiveGuildId, 'leaderboard'), icon: Trophy, label: 'Leaderboard', disabled: !xpPagesEnabled },
      withLockedState(
        { to: buildServerPath(routeLang, effectiveGuildId, 'embeds'), icon: MessageSquare, label: 'Messages' },
        { requiredScope: 'embeds_system' },
      ),
    ];

    const sections = [
      {
        title: 'Server',
        items: serverItems,
      },
      {
        title: 'Systems',
        items: [],
        systems,
      },
      {
        title: 'Settings',
        items: [
          withLockedState(
            { to: buildServerPath(routeLang, effectiveGuildId, 'server-settings'), icon: Settings, label: 'Server Settings' },
            { ownerOnly: true },
          ),
          withLockedState(
            { to: buildServerPath(routeLang, effectiveGuildId, 'admin-roles'), icon: KeyRound, label: 'Admin Roles' },
            { ownerOnly: true },
          ),
        ],
      },
    ];

    return sections.filter(
      (section) =>
        (Array.isArray(section.items) && section.items.length > 0) ||
        (Array.isArray(section.systems) && section.systems.length > 0)
    );
  }, [
    accessContext,
    logsSystemEnabled,
    routeLang,
    levelSystemEnabled,
    xpPagesEnabled,
    effectiveGuildId,
    pathContext.scope,
    systemStatus.automodEnabled,
    systemStatus.commandsEnabled,
    systemStatus.autoRolesEnabled,
    systemStatus.welcomeEnabled,
    systemStatus.tempVoiceEnabled,
    t,
  ]);

  const handleLogout = async () => {
    await logout();
    navigate(buildLocaleRootPath(routeLang), { replace: true });
  };

  const collapseContentTransition = useMemo(() => ({ duration: 0.25 }), []);
  const collapseContentMotion = useMemo(
    () => ({
      initial: { height: 0, opacity: 0 },
      animate: { height: 'auto', opacity: 1 },
      exit: { height: 0, opacity: 0 },
    }),
    []
  );

  const dividerEdgeClass = isRtl ? 'border-l' : 'border-r';
  const mobileEdgeClass = isRtl ? 'right-0 lg:right-auto' : 'left-0 lg:left-auto';
  const mobileMenuEdgeClass = isRtl ? 'right-4' : 'left-4';
  const collapsedX = isRtl ? SIDEBAR_WIDTH_EXPANDED : -SIDEBAR_WIDTH_EXPANDED;

  return (
    <LayoutGroup id={animationGroup}>
      <Button
        variant="ghost"
        size="icon"
        className={`tns-mobile-menu fixed top-4 ${mobileMenuEdgeClass} z-[60] lg:hidden`}
        onClick={() => setCollapsed((prev) => !prev)}
        aria-label={collapsed ? t('فتح القائمة', 'Open menu') : t('إغلاق القائمة', 'Close menu')}
        aria-expanded={!collapsed}
        data-state={collapsed ? 'closed' : 'open'}
        data-testid="mobile-menu-btn"
      >
        {collapsed ? <Menu className="w-6 h-6" /> : <X className="w-6 h-6" />}
      </Button>

      <div
        data-state={collapsed ? 'closed' : 'open'}
        className="fixed inset-0 bg-black/50 z-30 lg:hidden tns-sidebar-overlay"
        onClick={() => setCollapsed(true)}
      />

      <motion.aside
        initial={false}
        animate={{
          x: isDesktop ? 0 : collapsed ? collapsedX : 0,
          width: isDesktop ? (collapsed ? 0 : SIDEBAR_WIDTH_EXPANDED) : SIDEBAR_WIDTH_EXPANDED,
        }}
        transition={wrapperTransition}
        style={{ overflow: 'hidden', pointerEvents: collapsed ? 'none' : 'auto' }}
        data-state={collapsed ? 'closed' : 'open'}
        className={`tns-sidebar fixed top-0 bottom-0 ${mobileEdgeClass} lg:sticky lg:top-0 lg:bottom-auto lg:h-screen z-50 bg-background ${dividerEdgeClass} ${collapsed ? 'border-transparent' : 'border-border'} flex flex-col`}
      >
        <motion.div
          initial={false}
          animate={{ opacity: collapsed ? 0 : 1 }}
          transition={{ duration: 0.16, delay: collapsed ? 0 : isDesktop ? 0.22 : 0.08 }}
          style={{ width: SIDEBAR_WIDTH_EXPANDED }}
          className="flex flex-col h-full"
        >
          <div className="tns-sidebar-brand-row px-4 border-b border-border">
            <DashboardBrand />
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
            {navSections.map((section) => {
              const sectionKey = section.id || section.title;
              if (!section.title) {
                return (
                  <div key={sectionKey} className="w-full space-y-1">
                    {!collapsed ? renderNavItems(section.items) : null}
                  </div>
                );
              }

              const isOpen = Boolean(sectionOpen[sectionKey]);
              const showContent = !collapsed && isOpen;

              return (
                <div key={sectionKey} className="w-full">
                  <button
                    type="button"
                    onClick={() =>
                      setSectionOpen((prev) => ({ ...prev, [sectionKey]: !Boolean(prev[sectionKey]) }))
                    }
                    dir={isRtl ? 'rtl' : 'ltr'}
                    className={`w-full px-4 py-2 rounded-lg hover:bg-secondary/40 transition-colors duration-200 ease-tns flex items-center ${
                      collapsed ? 'justify-center px-2' : 'justify-between gap-2'
                    } ${isRtl ? 'text-right' : 'text-left'}`}
                    aria-expanded={isOpen}
                    data-testid={`sidebar-section-${sectionKey}`}
                  >
                    <motion.span
                      className={`${collapsed ? 'max-w-0 overflow-hidden' : 'min-w-0 truncate flex-1'} ${isRtl ? 'text-right' : 'text-left'}`}
                      animate={expandCollapseMotion(collapsed)}
                      transition={labelTransition}
                    >
                      {sectionTitle(section.title)}
                    </motion.span>
                    {!collapsed ? (
                      <motion.div
                        className="shrink-0"
                        initial={false}
                        animate={{ rotate: isOpen ? 180 : 0, opacity: 1 }}
                        transition={wrapperTransition}
                      >
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </motion.div>
                    ) : null}
                  </button>

                  <AnimatePresence initial={false}>
                    {showContent && (
                      <motion.div
                        {...collapseContentMotion}
                        transition={collapseContentTransition}
                        style={{ overflow: 'hidden' }}
                        className=""
                      >
                        <div className="pt-2 space-y-1">
                          {renderNavItems(section.items)}

                          {section.systems
                            ? section.systems.map((sys) => {
                                const sysIsOpen = Boolean(systemOpen[sys.id]);
                                return (
                                  <div key={sys.id} className="space-y-1">
                                    <div className="tns-sidebar-system-row flex items-stretch gap-2 px-4 py-2 rounded-lg hover:bg-secondary/40 transition-colors duration-200 ease-tns">
                                      <NavLink
                                        to={sys.to}
                                        onClick={(event) => {
                                          if (!sys.locked) return;
                                          event.preventDefault();
                                          event.stopPropagation();
                                          showLockedToast();
                                        }}
                                        aria-disabled={sys.locked}
                                        title={sys.locked ? sys.lockReason : undefined}
                                        className={({ isActive }) =>
                                          `flex items-center gap-2 min-w-0 flex-1 rounded-md px-2 py-1.5 transition-colors duration-200 ease-tns ${
                                            sys.locked
                                              ? 'text-muted-foreground/60 bg-secondary/15 opacity-70 cursor-not-allowed'
                                              : isActive
                                              ? 'bg-secondary/50 text-foreground'
                                              : 'text-muted-foreground hover:text-foreground'
                                          }`
                                        }
                                        data-testid={`sidebar-system-${sys.id}`}
                                      >
                                        {({ isActive }) => <>
                                          <NavHighlight active={isActive && !sys.locked} group="systems" />
                                          <sys.icon className="w-4 h-4 shrink-0" />
                                          <span className="font-body text-sm whitespace-nowrap flex-1">{itemLabel(sys.label)}</span>
                                          {sys.locked ? <Lock className="w-3.5 h-3.5 shrink-0 text-muted-foreground/80" /> : null}
                                        </>}
                                      </NavLink>

                                      <div className="flex items-center justify-center gap-2 shrink-0">
                                        <span
                                          className={`tns-system-status inline-block h-2.5 w-2.5 rounded-full shrink-0 ${
                                            sys.enabled === true
                                              ? 'bg-emerald-400 ring-2 ring-emerald-400/40 shadow-[0_0_14px_rgba(52,211,153,0.9)]'
                                              : sys.enabled === false
                                                ? 'bg-muted-foreground/40 ring-2 ring-muted-foreground/10'
                                                : 'bg-amber-300/80 ring-2 ring-amber-300/30 shadow-[0_0_10px_rgba(252,211,77,0.5)]'
                                          }`}
                                        />

                                        {Array.isArray(sys.items) && sys.items.length > 0 ? (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setSystemOpen((prev) => ({
                                                ...prev,
                                                [sys.id]: !Boolean(prev[sys.id]),
                                              }));
                                            }}
                                            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0 transition-colors duration-200 ease-tns active:scale-95 transform-gpu"
                                            aria-label={`${tr('sidebar.common.toggle', 'Toggle')} ${itemLabel(sys.label)}`}
                                            aria-expanded={sysIsOpen}
                                            data-testid={`sidebar-system-toggle-${sys.id}`}
                                          >
                                            <motion.div
                                              initial={false}
                                              animate={{ rotate: sysIsOpen ? 180 : 0 }}
                                              transition={wrapperTransition}
                                            >
                                              <ChevronDown className="h-4 w-4" />
                                            </motion.div>
                                          </button>
                                        ) : null}
                                      </div>
                                    </div>

                                    <AnimatePresence initial={false}>
                                      {sysIsOpen && Array.isArray(sys.items) && sys.items.length > 0 && (
                                        <motion.div
                                          {...collapseContentMotion}
                                          transition={collapseContentTransition}
                                          style={{ overflow: 'hidden' }}
                                          className="tns-sidebar-subpages"
                                        >
                                          <div className="space-y-1 pb-2">
                                            {sys.items.map((child) => (
                                              <NavLink
                                                key={child.to}
                                                to={child.to}
                                                onClick={(event) => {
                                                  if (!child.locked) return;
                                                  event.preventDefault();
                                                  event.stopPropagation();
                                                  showLockedToast();
                                                }}
                                                aria-disabled={child.locked}
                                                aria-current={child.section
                                                  ? (!child.locked && pathContext.subPath === 'systems/temp-voice' && child.section === voiceSection ? 'page' : false)
                                                  : undefined}
                                                title={child.locked ? child.lockReason : undefined}
                                                className={({ isActive }) =>
                                                  `flex items-center gap-2 px-10 py-1.5 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors duration-200 ease-tns ${
                                                    child.locked
                                                      ? 'text-muted-foreground/60 bg-secondary/15 opacity-70 cursor-not-allowed'
                                                      : childIsActive(child, isActive)
                                                      ? 'bg-primary/10 text-primary'
                                                      : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                                                  }`
                                                }
                                                data-testid={`sidebar-system-child-${sys.id}`}
                                              >
                                                {({ isActive }) => <>
                                                  <NavHighlight active={childIsActive(child, isActive) && !child.locked} group="subpages" />
                                                  <child.icon className="w-3.5 h-3.5 shrink-0" />
                                                  <span className="font-body text-xs flex-1 text-start">
                                                    {itemLabel(child.label)}
                                                  </span>
                                                  {child.locked ? <Lock className="w-3 h-3 shrink-0 text-muted-foreground/80" /> : null}
                                                </>}
                                              </NavLink>
                                            ))}
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>

                                  </div>
                                );
                              })
                            : null}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          <div className="p-4 border-t border-border space-y-2">
            {/* User Profile Button */}
            <Button
              variant="ghost"
              onClick={() => {
                localStorage.removeItem('tns:selected_guild_id');
                localStorage.removeItem('tns:selected_guild_access');
                localStorage.removeItem('tns:selected_guild_scopes');
                dispatchGuildSelection(null);
                navigate(buildDashboardPath(routeLang, 'profile'));
              }}
              className={`w-full justify-start gap-3 ${collapsed ? 'justify-center px-2' : 'px-4'} ${pathContext.scope === 'dashboard' ? 'bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary' : ''}`}
              data-testid="sidebar-user-profile-btn"
            >
              {user?.avatar ? (
                <img
                  src={`https://cdn.discordapp.com/avatars/${user.user_id}/${user.avatar}.${String(user.avatar).startsWith('a_') ? 'gif' : 'png'}?size=64`}
                  alt={user.username}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <span className="text-sm font-medium">{user?.username?.[0]?.toUpperCase()}</span>
                </div>
              )}
              <motion.span
                className={`${collapsed ? 'max-w-0 overflow-hidden' : 'flex-1 text-left'}`}
                animate={expandCollapseMotion(collapsed)}
                transition={labelTransition}
              >
                {tr('sidebar.common.userProfile', 'User Profile')}
              </motion.span>
            </Button>

            {/* Logout Button */}
            <div className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-secondary/50 ${collapsed ? 'justify-center px-2' : ''}`}>
              {!collapsed ? (
                user?.avatar ? (
                  <img
                    src={`https://cdn.discordapp.com/avatars/${user.user_id}/${user.avatar}.${String(user.avatar).startsWith('a_') ? 'gif' : 'png'}`}
                    alt={user.username}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm">{user?.username?.[0]?.toUpperCase()}</span>
                  </div>
                )
              ) : null}
              <motion.div
                className={`${collapsed ? 'max-w-0 overflow-hidden' : 'flex-1 min-w-0'}`}
                animate={expandCollapseMotion(collapsed)}
                transition={labelTransition}
              >
                <p className="text-sm font-medium truncate">{user?.username}</p>
                <p className="text-xs text-muted-foreground">
                  {user?.is_admin ? tr('common.admin', 'Admin') : tr('sidebar.common.user', 'User')}
                </p>
              </motion.div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="hover:text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.aside>
    </LayoutGroup>
  );
};


